import * as React from 'react';
import { Button, HStack, VStack, Text, Box, Separator } from '@chakra-ui/react';
import { Modal } from 'core/ui-next/modal';
import { AcceptActionButton, BackActionButton, Content, Filler, Footer, Header, Keypad, KeypadActionButton, LargeDeviceOnly, NextActionButton, PhoneOnly, PosScreen, ThemeManager } from 'pos/ui';
import { type CompositionResult, type CompositionParams, type CompositionState } from './Composition';
import * as AddinComposer from './AddinComposer';
import { useCompositionWizardMachine } from './ProductWizardMachine';
import { AddinRoleAdditionMode, CurrentActionDisplayProfileDTO, Res, ValueButtonSize } from 'core/api';
import { buildSoldProduct } from '../buildSoldProduct';
import * as UI from 'core/ui';
import { ProductButtonProps } from '../../sale/ProductButton';
import { StepDefinition, StepId, StepStatus } from './StepDefinition';
import { z } from 'zod';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { buildDetailBlocks } from '../compositionPanelDetailsLine';
import { SvgIcon } from 'core/ui-next';
import { Check } from 'core/img';
import { Result } from 'core/infrastructure';
import { zInt } from 'core/infrastructure-next';

function StepButton(props: {
	step: StepDefinition;
	status: StepStatus;
	selected: boolean;
	onGoTo: (stepId: StepId) => void;
	isFirst: boolean;
	isLast: boolean;
}) {
	const { step, status, selected, onGoTo, isFirst, isLast } = props;

	const disabled = status === 'pending' && !selected;

	let bgColor = disabled ? { base: 'neutral.100', _dark: 'neutral.900' } : { base: 'white', _dark: 'neutral.200' };
	let borderColor = status === 'completed' ? 'transparent' : disabled ? { base: 'neutral.300', _dark: 'neutral.700' } : selected ? { base: 'blue.800', _dark: 'blue.700' } : 'transparent';
	let fontColor = disabled ? { base: 'neutral.300', _dark: 'neutral.700' } : { base: 'neutral.800', _dark: 'neutral.800' };

	if (selected) {
		bgColor = { base: 'primary', _dark: 'primary' };
		fontColor = { base: 'white', _dark: 'white' };
	} else if (status === 'completed' || status === 'review') {
		bgColor = { base: 'blue.200', _dark: 'blue.700' };
		fontColor = { base: 'neutral.800', _dark: 'white' };
	}

	return (
		<Button
			bg={borderColor}
			color={fontColor}
			disabled={disabled}
			maxH={selected ? '10' : '9'}
			minW={'36'}
			display={'flex'}
			gap={'0.5'}
			alignItems={'center'}
			justifyContent={'center'}
			overflow={'visible'}
			isolation={'isolate'}
			data-selected={selected}
			opacity={status === 'completed' || (status === 'review' && !selected) ? 0.7 : disabled ? 0.5 : 1}
			onClick={() => onGoTo(step.id)}
			_after={{
				content: '" "',
				position: 'absolute',
				inset: '-2px',
				bg: bgColor,
				clipPath: {
					base: isFirst ? 'polygon(0% 0%, calc(100% - 11px) 0%, 99.5% 50%, calc(100% - 11px) 100%, 0% 100%)' :
						isLast ? 'polygon(1% 0%, 100% 0%, 100% 100%, 1% 100%, 12px 50%)' :
							'polygon(1% 0%, calc(100% - 11.5px) 0%, 99.5% 50%, calc(100% - 11.5px) 100%, 1% 100%, 12px 50%)'
				},
				zIndex: -1,
				margin: '3px',
				borderRadius: '3px'
			}}
			clipPath={
				isFirst ? 'polygon(0% 0%, calc(100% - 11.5px) 0%, 100% 50%, calc(100% - 11.5px) 100%, 0% 100%)' :
					isLast ? 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 12px 50%)' :
						'polygon(0% 0%, calc(100% - 12px) 0%, 100% 50%, calc(100% - 12px) 100%, 0% 100%, 12px 50%)'
			}
		>
			{status === 'completed' && <SvgIcon color={{ base: 'info', _dark: 'blue.500' }} src={Check} w={'4'} h={'4'} />}
			<Text>{step.label}</Text>
		</Button >
	);
}

function getProductLineDetailBlocks(composition: CompositionState): React.ReactNode | null {
	const selectedAddinsByRole = composition.addinsSelection.composition.selectedAddinsByRole;
	const addins = Object.values(selectedAddinsByRole).flat();

	return buildDetailBlocks({
		size: composition.size,
		color: composition.color,
		addins: addins,
		removedIngredients: composition.removedIngredients,
		lot: composition.lotSelection.selected,
		notes: composition.preparationNotes,
		isProductComposition: true,
	});
}

function ProductCompositionPanel({ composition }: { composition: CompositionState }): React.ReactElement {
	return (
		<VStack
			width={'full'}
			padding={'2'}
			gap={'1'}
			bg={{ base: 'white', _dark: 'neutral.800' }}
			borderRadius={'md'}
		>
			<HStack width={'full'} height={'full'} gap={'2'}>
				<Text display={'flex'} justifyContent={'flex-end'} minWidth={'10'} fontSize={'sm'}>{UI.Format.asShortQuantity(composition.quantity)}</Text>
				<Text fontSize={'sm'} lineClamp={2}>{composition.selectedProduct?.SaleFormatName}</Text>
			</HStack>
			{getProductLineDetailBlocks(composition)}
		</VStack>
	);
}

function getCompositionsFromDifferentAddinPerUnit(productSettings: CompositionParams, state: CompositionState): CompositionResult[] {

	// Aquí lo que estamos haciendo es en base a los añadidos generar varias líneas. Ejemplo:
	// Si el usuario con el teclado marca 5 y luego pincha sobre el JB y ha pedido lo siguiente:
	//     JB
	//		 c/ Coca Cola
	//       c/ Coca Cola
	//       c/ Fanta
	// El resultado final será
	//     2 x JB c/ Coca Cola
	//     1 x JB c/ Fanta
	//     2 x JB

	const { groupName, type, preparationOrderId, preparationTypeId } = productSettings;
	const { addinsSelection, preparationNotes, removedIngredients, price, quantity, color, size, lotSelection: lot, selectedProduct: selectedFormat } = state;
	const addins = AddinComposer.getAddins(addinsSelection.composition);

	const remainingComposition =
	{
		product: buildSoldProduct({
			product: selectedFormat!,
			groupName,
			price,
			color,
			size,
			lot: lot.selected,
			preparationOrderId,
			preparationTypeId,
			type
		}),
		addins: [],
		preparationNotes,
		// En este modo configuración AddinRoleAdditionMode.ChooseDifferentAddinPerUnit, solo se permite un grupo de añadidos
		// por diseño en la administración, por eso usamos roles[0]
		// Sólo permito añadir sin cantidad cuando minAddins es cero, si no calculo el resto que me queda.
		quantity: addinsSelection.composition.roles[0].MinAddins === 0 ? quantity - addins.length : 0,
		removedIngredients
	};

	const compositions = addins.groupBy(x => x.SaleFormatId).map(group => ({
		product: buildSoldProduct({
			product: selectedFormat!,
			groupName,
			price,
			color,
			size,
			lot: lot.selected,
			preparationOrderId,
			preparationTypeId: selectedFormat!.PreparationTypeId,
			type
		}),
		addins: [group.value[0]],
		preparationNotes,
		quantity: group.value.length,
		removedIngredients: removedIngredients,

	})).concat([remainingComposition]).filter(x => x.quantity > 0);

	return compositions;
}

export function getCompositionsUsingSameAddinForAll(productSettings: CompositionParams, state: CompositionState): CompositionResult[] {

	const { groupName, type, preparationOrderId, preparationTypeId } = productSettings;
	const { addinsSelection, preparationNotes, removedIngredients, price, quantity, color, size, lotSelection: lot, selectedProduct: selectedFormat } = state;
	const addins = AddinComposer.getAddins(addinsSelection.composition);

	return [{
		product: buildSoldProduct({
			product: selectedFormat!,
			groupName,
			price,
			color,
			size,
			lot: lot.selected,
			preparationOrderId,
			preparationTypeId,
			type
		}),
		addins,
		preparationNotes,
		quantity,
		removedIngredients
	}];
}

export function getCompositionResultsFrom(productSettings: CompositionParams, state: CompositionState): CompositionResult[] {

	return state.addinsSelection.mode === AddinRoleAdditionMode.ChooseDifferentAddinPerUnit
		? getCompositionsFromDifferentAddinPerUnit(productSettings, state)
		: getCompositionsUsingSameAddinForAll(productSettings, state);
}

type Props = Modal.Actions<CompositionResult[]> & {
	params: CompositionParams;
	initialStepsStatus?: Exclude<StepStatus, 'completed'>
	initialComposition: CompositionState;
	initialSteps: StepDefinition[];
	profile: CurrentActionDisplayProfileDTO;
};

const getProfileButtonSize = (profile: CurrentActionDisplayProfileDTO): ProductButtonProps<any>['size'] => {

	const buttonSize = profile.ButtonSize;

	const largeDeviceSizes: Record<ValueButtonSize, ProductButtonProps<any>['size']> = {
		[ValueButtonSize.Small]: 'sm',
		[ValueButtonSize.Medium]: 'md',
		[ValueButtonSize.Large]: 'lg'
	};

	const smallDeviceSizes: Record<ValueButtonSize, ProductButtonProps<any>['size']> = {
		[ValueButtonSize.Small]: 'phone-md',
		[ValueButtonSize.Medium]: 'phone-md',
		[ValueButtonSize.Large]: 'phone-lg'
	};

	const size = ThemeManager.isPhone() ? smallDeviceSizes[buttonSize] : largeDeviceSizes[buttonSize];

	return size;
};

const quantitySchema = z.object({
	qty: zInt({ nonNegative: true, messageInvalid: Res.Msg.MSG_INVALID_FORMAT() }).nullable()
});

type QuantityState = z.infer<typeof quantitySchema>;

export function ProductWizardScreen({ cancel, close, params, initialStepsStatus, initialSteps, initialComposition, profile }: Props) {

	const [showKeypad, setShowKeypad] = React.useState(false);

	const formRef = React.useRef<FormRef<QuantityState>>(null);

	const onClose = React.useCallback(
		(state: CompositionState) => {
			close(getCompositionResultsFrom(params, state));
		},
		[close, params]
	);

	const { state, machine } = useCompositionWizardMachine({
		params,
		initialStepsStatus,
		initialSteps,
		initialComposition,
		onClose,
	});

	const hasOnlyOneStep = state.steps.length === 1;
	const currentStep = state.currentStep;

	const sendFocusToQty = React.useCallback(() => {
		setTimeout(() => formRef.current?.setFocus?.('qty'));
	}, []);

	const restoreKeyboardQuantity = React.useCallback(() => {
		formRef.current?.setValue('qty', null);
		sendFocusToQty();
	}, [sendFocusToQty]);

	React.useEffect(() => { showKeypad && restoreKeyboardQuantity(); }, [restoreKeyboardQuantity, showKeypad]);

	const onShowKeyPad = React.useCallback(async () => {
		setShowKeypad(prev => !prev);
	}, []);

	const getIntroducedQuantity = React.useCallback(async () => {
		const result = formRef.current ? await formRef.current.getValue() : Result.ok({ qty: null });
		if (!result.ok) {
			sendFocusToQty();
			return Result.error();
		}

		const value = result.value.qty;
		return Result.ok(value);
	}, [sendFocusToQty]);

	const onCancel = React.useCallback(async () => cancel(), [cancel]);

	const onNext = React.useCallback(async () => machine.next(), [machine]);

	const onBack = React.useCallback(async () => machine.back(), [machine]);

	const onFinish = React.useCallback(async () => {
		machine.finish();
	}, [machine]);

	const { Form, DecimalInput } = useControlsFor<QuantityState>();

	const stepsContainerRef = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		if (stepsContainerRef.current) {
			const selected = stepsContainerRef.current.querySelector('[data-selected="true"]');
			selected?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
		}
	}, [currentStep.id]);

	const [isCompositionVisible, setIsCompositionVisible] = React.useState(false);

	const toggleCompositionVisible = React.useCallback(() => setIsCompositionVisible(v => !v), []);

	//Al tener mas steps y renderizar el stepper superior desplaza el contenido hacia abajo, lo que hace
	//que el contenido de se meta por detrás de el footer, con el margin nos ajustamos al tamaño de el footer
	//y asi no se esconde.
	const marginBottomAdjustment = hasOnlyOneStep ? '0' : ThemeManager.isLargeDevice() ? '18' : showKeypad ? '0' : '16';

	return (
		<PosScreen size='medium' screenId={'COMPOSE_PRODUCT_SCREEN'} useKeyPad={false}>

			<Header title={Res.Web.COMPOSE_PRODUCT_TITLE()} onClose={cancel} wizardHeader={true} isCompositionVisible={toggleCompositionVisible} />

			<Content>
				{isCompositionVisible ?
					<VStack
						width="full"
						height="full"
						bg={{ base: '{colors.neutral.100}', _dark: '{colors.neutral.900}' }}
						padding={'2'}
						gap={'2'}
						minH={0}
						align="stretch"
						flexShrink={0}
					>
						<VStack flex="1" minH={0} overflow="auto" gap={'6'}>
							<Box fontSize={'md'} fontWeight={'semibold'} width={'full'} justifyContent={'flex-start'}>
								<Text fontWeight={'semibold'} fontSize={'md'}>
									{state.composition.selectedProduct?.SaleFormatName}
								</Text>
							</Box>
							<ProductCompositionPanel composition={state.composition} />
						</VStack>
					</VStack>
					:
					<VStack width="full" height="full" bg={{ base: '{colors.neutral.100}', _dark: '{colors.neutral.900}' }}>
						<HStack width="full" height="full" paddingX="2" paddingY="3">
							<LargeDeviceOnly>
								<VStack minW={'79'} maxW={'79'} height={'full'} overflow={'auto'} gap={'6'}>
									{state.composition.selectedProduct?.SaleFormatName &&
										<VStack width={'full'} height="full" minH={0} align="stretch" flexShrink={0}>
											<VStack flex="1" minH={0} overflow="auto" gap={'6'}>
												<Box fontSize={'md'} fontWeight={'semibold'} width={'full'} justifyContent={'flex-start'}>
													<Text fontWeight={'semibold'} fontSize={'md'}>
														{state.composition.selectedProduct?.SaleFormatName}
													</Text>
												</Box>
												<ProductCompositionPanel composition={state.composition} />
											</VStack>
											{
												showKeypad
													? <Box flexShrink={0}>
														<Form initialValue={{ qty: null }} schema={quantitySchema} ref={formRef}>
															<DecimalInput linkTo='qty' placeholder='0' />
															<Keypad isFullScreen={false} isLargeDevice={true} />
														</Form>
													</Box>
													: null
											}
										</VStack>
									}
								</VStack>
								<Separator height={'full'} orientation={'vertical'} borderColor={'transparent'} bg={{ base: '{colors.neutral.300}', _dark: '{colors.neutral.300}' }} width={'1.25px'} />
							</LargeDeviceOnly>

							<VStack flexGrow={1} height={'full'} width={'full'}>
								{
									hasOnlyOneStep
										? null
										: <>
											<Box
												position="sticky"
												top={0}
												zIndex={1}
												width="full"
												flexShrink={0}
												display={'grid'}
												justifyContent={'space-around'}
											>
												<HStack
													padding="2"
													gap={'0'}
													width={'full'}
													overflowX={'auto'}
													overflowY={'hidden'}
													flexShrink={0}
													ref={stepsContainerRef}
												>
													{state.steps.map((step, index) => (
														<StepButton
															key={step.id}
															step={step}
															status={machine.getStatus(step.id)}
															selected={step.id === currentStep.id}
															isFirst={index === 0}
															isLast={index === state.steps.length - 1}
															onGoTo={machine.tryTransitionTo}
														/>
													))}
												</HStack>
											</Box>
											<Separator height={'1.25px'} orientation={'horizontal'} borderColor={'transparent'} bg={{ base: '{colors.neutral.300}', _dark: '{colors.neutral.300}' }} width={'full'} />
										</>
								}
								<VStack width={'full'} height="full" minH={0} align="stretch" flexShrink={0}>
									<VStack flex="1" minH={0} overflow="auto" gap={'6'} marginBottom={marginBottomAdjustment}>
										<currentStep.Component
											settings={params}
											getIntroducedQty={getIntroducedQuantity}
											resetIntroducedQty={restoreKeyboardQuantity}
											composition={state.composition}
											setComposition={machine.setComposition}
											requestNext={machine.next}
											status={machine.getStatus(currentStep.id)}
											setSteps={machine.setSteps}
											buttonSize={getProfileButtonSize(profile)}
										/>
									</VStack>
									<PhoneOnly>
										{
											showKeypad
												? <Box flexShrink={0} marginBottom={hasOnlyOneStep ? '0' : '16'}>
													<Form initialValue={{ qty: null }} schema={quantitySchema} ref={formRef}>
														<DecimalInput linkTo='qty' placeholder='0' />
														<Keypad isFullScreen={false} isLargeDevice={true} />
													</Form>
												</Box>
												: null
										}
									</PhoneOnly>
								</VStack>
							</VStack>
						</HStack>
					</VStack>
				}
			</Content>

			<Footer>
				{hasOnlyOneStep ?
					<>
						<PhoneOnly>
							{isCompositionVisible ?
								<BackActionButton action={async () => toggleCompositionVisible()} />
								:
								<BackActionButton action={onCancel} />
							}
						</PhoneOnly>
						{currentStep.useKeyPad && !isCompositionVisible ?
							<KeypadActionButton action={onShowKeyPad} />
							: null
						}
						<Filler />

						{!isCompositionVisible ?
							<AcceptActionButton action={onFinish} />
							: null
						}
					</>
					: <>
						<PhoneOnly>
							{isCompositionVisible ?
								<BackActionButton action={async () => toggleCompositionVisible()} />
								:
								<BackActionButton action={state.steps[0]?.id === currentStep.id ? onCancel : onBack} />
							}
						</PhoneOnly>
						{currentStep.useKeyPad && !isCompositionVisible ?
							<KeypadActionButton action={onShowKeyPad} />
							: null
						}
						{state.isLastStep ?
							<>
								<Filler />
								{!isCompositionVisible ?
									<AcceptActionButton action={onFinish} />
									: null
								}
							</>
							: <>
								<Filler />
								{!isCompositionVisible ?
									<NextActionButton action={onNext} />
									: null
								}
							</>
						}
					</>
				}
			</Footer>

		</PosScreen>
	);
}
